import mixpanel from 'mixpanel-browser';

export interface MixpanelConfig {
  token: string;
  enabled: boolean;
  debug?: boolean;
  sessionReplaySampleRate?: number;
  /**
   * Idle timeout in minutes before ending a replay session.
   * When idle timeout is reached, the current replay ends and a new one starts when activity resumes.
   * Default: 15 minutes (recommended for most apps to avoid replays with long dead time).
   */
  idleTimeoutMinutes?: number;
}

class MixpanelService {
  private initialized = false;
  private enabled = false;

  initialize(config: MixpanelConfig): boolean {
    if (this.initialized) {
      return this.enabled;
    }

    const token = config.token.trim();
    if (!config.enabled || token === '') {
      this.enabled = false;
      this.initialized = true;
      return false;
    }

    try {
      mixpanel.init(token, {
        debug: config.debug ?? import.meta.env.DEV,
        record_sessions_percent: config.sessionReplaySampleRate ?? 50,
        // End replay session after idle timeout to prevent replays with long dead time
        // Default: 15 minutes (900,000 ms) - recommended for most apps
        record_idle_timeout_ms: (config.idleTimeoutMinutes ?? 15) * 60 * 1000,
        record_mask_text_selector: '.sensitive, .pii, input[type="password"], input[type="email"]',
        // Block artifact content to prevent PII from being captured in session replays
        // Artifacts contain sensitive coaching conversation summaries and member data
        record_block_selector: '.private-data, .secret, .mp-block, .artifact-content',
        ignore_dnt: true,
      });
      this.enabled = true;
      this.initialized = true;
      return true;
    } catch (error) {
      console.error('[Mixpanel] failed to initialize', error);
      this.enabled = false;
      this.initialized = true;
      return false;
    }
  }

  identify(personId: string): void {
    if (!this.enabled) return;

    const distinctId = personId.trim();
    if (distinctId === '') return;

    try {
      mixpanel.identify(distinctId);
    } catch (error) {
      console.error('[Mixpanel] failed to identify', error);
    }
  }

  reset(): void {
    if (!this.enabled) return;

    try {
      mixpanel.reset();
    } catch (error) {
      console.error('[Mixpanel] failed to reset', error);
    }
  }

  stopRecording(): void {
    if (!this.enabled) return;

    try {
      mixpanel.stop_session_recording();
    } catch (error) {
      console.error('[Mixpanel] failed to stop recording', error);
    }
  }

  startRecording(): void {
    if (!this.enabled) return;

    try {
      mixpanel.start_session_recording();
    } catch (error) {
      console.error('[Mixpanel] failed to start recording', error);
    }
  }
}

export const mixpanelService = new MixpanelService();
